import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GripVertical, Clock, CheckCircle2, Edit2 } from 'lucide-react';
import { format } from 'date-fns';
import { useCountdown } from '../../hooks/useCountdown';

export const DraggableInterest = ({ interest, onDragEnd, onEdit, onAchieve, containerRef, allInterests, reminderTriggered }) => {
  const [isDragging, setIsDragging] = useState(false);
  const nodeRef = useRef(null);
  const dragStartRef = useRef({ mouseX: 0, mouseY: 0, elemX: 0, elemY: 0 });

  const { countdown, isOverdue, showCountdown } = useCountdown(
    interest.deadline,
    interest.reminder_minutes,
    reminderTriggered
  );

  const handleDragStart = (event, info) => {
    setIsDragging(true);
    if (containerRef.current) {
      const container = containerRef.current.getBoundingClientRect();
      dragStartRef.current = {
        mouseX: info.point.x - container.left,
        mouseY: info.point.y - container.top,
        startPctX: interest.position_x || 20,
        startPctY: interest.position_y || 20,
        containerWidth: container.width,
        containerHeight: container.height
      };
    }
  };

  const handleDrag = () => {};

  const handleDragEnd = (event, info) => {
    setIsDragging(false);
    if (!containerRef.current) return;

    const { startPctX, startPctY, containerWidth, containerHeight } = dragStartRef.current;
    const offsetPctX = (info.offset.x / containerWidth) * 100;
    const offsetPctY = (info.offset.y / containerHeight) * 100;

    let x = startPctX + offsetPctX;
    let y = startPctY + offsetPctY;

    x = Math.max(5, Math.min(88, x));
    y = Math.max(5, Math.min(93, y));

    // Collision detection
    const minDistance = 10;
    const maxIterations = 20;
    let iterations = 0;
    let hasCollision = true;

    while (hasCollision && iterations < maxIterations) {
      hasCollision = false;
      for (const other of allInterests) {
        if (other.interest_id === interest.interest_id) continue;
        const otherX = other.position_x || 20;
        const otherY = other.position_y || 20;
        const dx = x - otherX;
        const dy = y - otherY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < minDistance) {
          hasCollision = true;
          if (distance > 0) {
            const pushX = (dx / distance) * (minDistance - distance + 2);
            const pushY = (dy / distance) * (minDistance - distance + 2);
            x = Math.max(5, Math.min(88, x + pushX));
            y = Math.max(5, Math.min(93, y + pushY));
          } else {
            x = Math.max(5, Math.min(88, x + (Math.random() - 0.5) * minDistance));
            y = Math.max(5, Math.min(93, y + (Math.random() - 0.5) * minDistance));
          }
          break;
        }
      }
      iterations++;
    }

    let quadrant;
    if (y < 50) {
      quadrant = x < 50 ? 'q1' : 'q2';
    } else {
      quadrant = x < 50 ? 'q3' : 'q4';
    }

    onDragEnd(interest.interest_id, x, y, quadrant);
  };

  const posX = Math.max(5, Math.min(88, interest.position_x || 20));
  const posY = Math.max(5, Math.min(93, interest.position_y || 20));
  const formatTime = (num) => String(num).padStart(2, '0');

  return (
    <motion.div
      key={`${interest.interest_id}-${posX}-${posY}`}
      ref={nodeRef}
      drag
      dragMomentum={false}
      dragElastic={0}
      onDragStart={handleDragStart}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      initial={false}
      animate={{ x: 0, y: 0 }}
      style={{
        position: 'absolute',
        left: `${posX}%`,
        top: `${posY}%`,
        transform: 'translate(-50%, -50%)',
        zIndex: isDragging ? 100 : (showCountdown ? 50 : 10)
      }}
      whileDrag={{ scale: 1.05, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}
      className="cursor-grab active:cursor-grabbing touch-none select-none pointer-events-auto"
      data-testid={`interest-card-${interest.interest_id}`}
    >
      <AnimatePresence>
        {showCountdown && countdown && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.9 }}
            className={`
              absolute -top-10 left-1/2 -translate-x-1/2 px-2 py-1 rounded-md text-[10px] font-mono font-bold
              shadow-lg border whitespace-nowrap z-20
              ${isOverdue 
                ? 'bg-rose-500 text-white border-rose-600 animate-pulse' 
                : 'bg-amber-500 text-white border-amber-600'
              }
            `}
            data-testid={`countdown-${interest.interest_id}`}
          >
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>
                {isOverdue ? '−' : ''}
                {countdown.hours > 0 && `${formatTime(countdown.hours)}:`}
                {formatTime(countdown.minutes)}:{formatTime(countdown.seconds)}
              </span>
            </div>
            <div className="text-[8px] text-center opacity-80">
              {isOverdue ? 'OVERDUE' : 'remaining'}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className={`
        group relative px-2 py-1.5 rounded-lg bg-card/90 backdrop-blur-sm border border-border 
        shadow-sm hover:shadow-md transition-shadow duration-200 max-w-[140px]
        ${isDragging ? 'shadow-xl' : ''}
        ${showCountdown && isOverdue ? 'border-rose-500 border-2 ring-2 ring-rose-500/30' : ''}
        ${showCountdown && !isOverdue ? 'border-amber-500 border-2 ring-2 ring-amber-500/30' : ''}
        ${!showCountdown && interest.deadline ? 'border-l-2 border-l-amber-500' : ''}
      `}>
        <div className="flex items-start gap-1">
          <GripVertical className="w-3 h-3 text-muted-foreground flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-xs leading-tight line-clamp-2">{interest.title}</p>
            {interest.deadline && !showCountdown && (
              <div className="flex items-center gap-1 mt-0.5">
                <Clock className="w-2.5 h-2.5 text-amber-500" />
                <span className="text-[9px] text-amber-600 dark:text-amber-400">
                  {format(new Date(interest.deadline), 'MMM d, HH:mm')}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <div className="bg-card rounded-full shadow-md border border-border p-0.5">
            <button
              onClick={(e) => { e.stopPropagation(); onAchieve(); }}
              className="p-1 rounded-full hover:bg-emerald-100 dark:hover:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400"
              title="Mark as achieved"
              data-testid={`achieve-btn-${interest.interest_id}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="bg-card rounded-full shadow-md border border-border p-0.5">
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="p-1 rounded-full hover:bg-muted"
              title="Edit"
              data-testid={`edit-btn-${interest.interest_id}`}
            >
              <Edit2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
